<script lang="ts">
	import { untrack } from 'svelte';
	import { format, parseISO } from 'date-fns';
	import type { ExamSession, ExamSessionsFilter } from '$lib/types/examSessions';
	import { MapPin, Calendar, Clock, ExternalLink, ChevronDown, ChevronUp } from 'lucide-svelte';

	const {
		filter: filterProp,
		maxSessions: maxSessionsProp = 5,
		showMoreFilters: showMoreFiltersProp = false
	} = $props<{
		filter?: ExamSessionsFilter;
		maxSessions?: number;
		showMoreFilters?: boolean;
	}>();

	// maxSessions is reactive — derived so it updates if the parent changes the prop
	const maxSessions = $derived(maxSessionsProp ?? 5);
	// showMoreFilters and currentFilter are locally mutable; untrack() makes the intent explicit
	let showMoreFilters = $state(untrack(() => showMoreFiltersProp ?? false));

	let sessions = $state<ExamSession[]>([]);
	let loading = $state(true);
	let error = $state<string | null>(null);
	let expandedSessions = $state(new Set<string>());
	let currentFilter = $state<ExamSessionsFilter>(untrack(() => filterProp ?? { team: 'NIVET' }));

	const distanceOptions = [15, 30, 60, 100];
	let selectedDistance = $state(30);

	$effect(() => {
		fetchSessions();
	});

	function toggleSessionDetails(sessionId: string) {
		if (expandedSessions.has(sessionId)) {
			expandedSessions.delete(sessionId);
		} else {
			expandedSessions.add(sessionId);
		}
	}

	async function fetchSessions() {
		loading = true;
		error = null;

		try {
			let url = 'https://hamstudy.org/api/v1/sessions?';
			const params = new URLSearchParams();

			if (currentFilter.team) {
				params.append('team', currentFilter.team);
			}

			if (currentFilter.zip) {
				params.append('zip', currentFilter.zip);
			}

			if (currentFilter.maxDistance) {
				params.append('maxDistance', currentFilter.maxDistance.toString());
			}

			url += params.toString();

			const response = await fetch(url);

			if (!response.ok) {
				throw new Error(`Failed to fetch exam sessions: ${response.statusText}`);
			}

			const data = await response.json();
			sessions = data;
		} catch (err) {
			error = err instanceof Error ? err.message : 'An unknown error occurred';
			console.error('Error fetching exam sessions:', err);
		} finally {
			loading = false;
		}
	}

	function formatDuration(seconds: number): string {
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);

		if (hours > 0) {
			return `${hours} hour${hours > 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} min` : ''}`;
		}

		return `${minutes} minutes`;
	}

	function updateFilter(zip?: string, distance?: number) {
		if (zip) currentFilter.zip = zip;
		if (distance) currentFilter.maxDistance = distance;

		// If we're showing sessions by zip code, remove the team filter
		if (currentFilter.zip && currentFilter.maxDistance) {
			currentFilter.team = undefined;
		} else {
			currentFilter.team = 'NIVET';
		}

		fetchSessions();
	}

	function formatDate(dateString: string): string {
		try {
			const date = parseISO(dateString);
			return format(date, 'EEE, MMM d, yyyy h:mm a');
		} catch (err) {
			console.error('Error formatting date:', err);
			return dateString;
		}
	}

	function getGoogleMapsUrl(address: string): string {
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
	}

	function getHamStudyUrl(infoLink: string): string {
		return `https://hamstudy.org${infoLink}`;
	}
</script>

<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
	<div class="mb-4 flex items-center justify-between">
		<h3 class="text-lg font-semibold dark:text-white">Upcoming Exam Sessions</h3>

		<button
			onclick={() => {
				showMoreFilters = !showMoreFilters;
			}}
			class="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
		>
			{showMoreFilters ? 'Hide Filter Options' : 'Search for Other Opportunities'}
		</button>
	</div>

	{#if showMoreFilters}
		<div class="mb-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
			<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<div>
					<label for="zip-code" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Zip Code
					</label>
					<input
						type="text"
						id="zip-code"
						placeholder="Enter zip code"
						class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
						value={currentFilter.zip || ''}
						oninput={(e) => (currentFilter.zip = e.currentTarget.value)}
					/>
				</div>

				<div>
					<label for="distance" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Distance (miles)
					</label>
					<div class="mt-1 flex flex-wrap gap-2">
						{#each distanceOptions as distance}
							<button
								class="rounded-full px-3 py-1 text-sm {selectedDistance === distance
									? 'bg-blue-600 text-white'
									: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'}"
								onclick={() => {
									selectedDistance = distance;
									updateFilter(currentFilter.zip, distance);
								}}
							>
								{distance}
							</button>
						{/each}
					</div>
				</div>
			</div>

			<div class="mt-4 flex justify-end">
				<button
					onclick={() => updateFilter(currentFilter.zip, selectedDistance)}
					class="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
				>
					Apply Filter
				</button>
			</div>
		</div>
	{/if}

	{#if loading}
		<div class="flex justify-center py-8">
			<div
				class="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"
			></div>
		</div>
	{:else if error}
		<div class="rounded-lg bg-red-50 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300">
			<p>Error loading exam sessions: {error}</p>
		</div>
	{:else if sessions.length === 0}
		<div class="rounded-lg bg-gray-50 p-4 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
			<p>No upcoming exam sessions found.</p>
		</div>
	{:else}
		<div class="max-h-80 overflow-y-auto pr-1">
			<div class="space-y-3">
				{#each sessions.slice(0, maxSessions) as session (session._id)}
					<div
						class="rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-700"
					>
						<div class="p-4">
							<div class="flex items-start justify-between">
								<div>
									<h4 class="font-medium dark:text-white">{session.summary}</h4>
									<div class="mt-1 flex items-center text-sm text-gray-600 dark:text-gray-300">
										<Calendar class="mr-1 h-4 w-4" />
										<span>{formatDate(session.date)}</span>
									</div>
									<div class="mt-1 flex items-center text-sm text-gray-600 dark:text-gray-300">
										<MapPin class="mr-1 h-4 w-4" />
										<span>{session.city}, {session.state}</span>
									</div>
								</div>

								<button
									onclick={() => toggleSessionDetails(session._id)}
									class="ml-2 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
								>
									{#if expandedSessions.has(session._id)}
										<ChevronUp class="h-5 w-5" />
									{:else}
										<ChevronDown class="h-5 w-5" />
									{/if}
								</button>
							</div>

							<div class="mt-3 flex flex-wrap gap-2">
								<a
									href={getHamStudyUrl(session.infoLink)}
									target="_blank"
									rel="noopener noreferrer"
									class="inline-flex items-center rounded-md bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
								>
									Register
									<ExternalLink class="ml-1 h-3 w-3" />
								</a>

								<a
									href={getGoogleMapsUrl(session.formatted_addr)}
									target="_blank"
									rel="noopener noreferrer"
									class="inline-flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
								>
									Map
									<ExternalLink class="ml-1 h-3 w-3" />
								</a>
							</div>

							{#if expandedSessions.has(session._id)}
								<div class="mt-3 border-t border-gray-200 pt-3 dark:border-gray-600">
									<div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
										<div>
											<p class="text-sm font-medium text-gray-700 dark:text-gray-300">Address:</p>
											<p class="text-sm text-gray-600 dark:text-gray-400">{session.address}</p>
										</div>

										<div>
											<p class="text-sm font-medium text-gray-700 dark:text-gray-300">Duration:</p>
											<div class="flex items-center text-sm text-gray-600 dark:text-gray-400">
												<Clock class="mr-1 h-4 w-4" />
												<span>{formatDuration(session.duration)}</span>
											</div>
										</div>

										{#if session.test_fee > 0}
											<div>
												<p class="text-sm font-medium text-gray-700 dark:text-gray-300">
													Test Fee:
												</p>
												<p class="text-sm text-gray-600 dark:text-gray-400">
													${session.test_fee.toFixed(2)}
												</p>
											</div>
										{/if}

										{#if session.prereg_required}
											<div class="sm:col-span-2">
												<p class="text-sm font-medium text-amber-600 dark:text-amber-400">
													Pre-registration required
												</p>
											</div>
										{/if}

										{#if session.notes}
											<div class="sm:col-span-2">
												<p class="text-sm font-medium text-gray-700 dark:text-gray-300">Notes:</p>
												<p class="text-sm text-gray-600 dark:text-gray-400">{session.notes}</p>
											</div>
										{/if}
									</div>
								</div>
							{/if}
						</div>
					</div>
				{/each}
			</div>
		</div>

		{#if !currentFilter.team}
			<div class="mt-4 flex justify-center">
				<button
					onclick={() => {
						currentFilter.team = 'NIVET';
						currentFilter.zip = undefined;
						currentFilter.maxDistance = undefined;
						fetchSessions();
					}}
					class="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
				>
					Show only DSARC sessions
				</button>
			</div>
		{:else if sessions.length > maxSessions}
			<div class="mt-4 flex justify-center">
				<a
					href="/resources/testing"
					class="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
				>
					View all exam sessions →
				</a>
			</div>
		{/if}
	{/if}
</div>
